import { Fragment, useState } from 'react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import type { CommitteePaperItem } from '@/hooks/use-committee-papers';
import { FileSearch, FileStack, Loader2, Minus, PlayCircle, Plus, UserPlus, Users } from 'lucide-react';

interface PapersTableProps {
  papers: CommitteePaperItem[];
  isLoading: boolean;
  onAssign: (paper: CommitteePaperItem) => void;
  onReassign: (paper: CommitteePaperItem) => void;
  onStartSecondRound: (paper: CommitteePaperItem) => void;
  onManageDocument: (paper: CommitteePaperItem) => void;
}

function statusBadge(status: CommitteePaperItem['status']) {
  switch (status) {
    case 'UNDER_EVALUATION':
      return (
        <Badge className="border border-yellow-500 bg-yellow-50 text-yellow-700 hover:bg-yellow-50">
          UNDER_EVALUATION
        </Badge>
      );
    case 'APPROVED_TO_PAY':
      return (
        <Badge className="border border-green-500 bg-green-50 text-green-700 hover:bg-green-50">
          APPROVED_TO_PAY
        </Badge>
      );
    case 'REJECTED':
      return (
        <Badge className="border border-red-500 bg-red-50 text-red-700 hover:bg-red-50">
          REJECTED
        </Badge>
      );
    case 'REVISION_REQUESTED':
      return (
        <Badge className="border border-amber-500 bg-amber-50 text-amber-700 hover:bg-amber-50">
          REVISION_REQUESTED
        </Badge>
      );
    default:
      return (
        <Badge className="border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-100">
          PENDING_REVIEW
        </Badge>
      );
  }
}

function progressColor(paper: CommitteePaperItem): string {
  if (paper.evaluatorsCount === 0) return 'bg-gray-300';
  if (paper.evaluatorsCount >= 3 && paper.status === 'REJECTED') return 'bg-red-500';
  if (paper.evaluatorsCount >= 3) return 'bg-green-500';
  return 'bg-[#c9a227]';
}

function resolveDocumentStatus(paper: CommitteePaperItem): 'ORIGINAL' | 'CLEAN_AVAILABLE' | 'PENDING_CLEAN' {
  if (paper.hasCleanVersion === true) {
    return 'CLEAN_AVAILABLE';
  }
  if (paper.hasCleanVersion === false) {
    return 'ORIGINAL';
  }

  const status = paper.document?.status || paper.documentStatus || null;
  if (status === 'CLEAN_AVAILABLE' || status === 'PENDING_CLEAN' || status === 'ORIGINAL') {
    return status;
  }

  if (paper.document?.hasCleanVersion === true) {
    return 'CLEAN_AVAILABLE';
  }
  if (paper.document?.hasCleanVersion === false) {
    return 'PENDING_CLEAN';
  }
  return 'ORIGINAL';
}

function documentBadge(status: 'ORIGINAL' | 'CLEAN_AVAILABLE' | 'PENDING_CLEAN') {
  if (status === 'CLEAN_AVAILABLE') {
    return (
      <span className="whitespace-nowrap rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase text-blue-600">
        CLEAN disponible
      </span>
    );
  }

  if (status === 'PENDING_CLEAN') {
    return (
      <span className="whitespace-nowrap rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase text-amber-700">
        CLEAN v2 pendiente
      </span>
    );
  }

  return (
    <span className="whitespace-nowrap rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-semibold uppercase text-slate-700">
      ORIGINAL
    </span>
  );
}

function resolveAssignBlock(paper: CommitteePaperItem): boolean {
  if (
    paper.documentStatus === 'ORIGINAL'
    || paper.documentStatus === 'PENDING_CLEAN'
    || paper.document?.status === 'ORIGINAL'
    || paper.document?.status === 'PENDING_CLEAN'
  ) {
    return true;
  }

  if (paper.hasCleanVersion === false || paper.document?.hasCleanVersion === false) {
    return true;
  }

  if (paper.canAssignEvaluators === false) {
    return true;
  }

  if (paper.assignmentPolicy?.canAssign === false) {
    return true;
  }

  const cleanRequired = paper.requiresCleanForAssignment === true || paper.assignmentPolicy?.requireCleanPaper === true;
  if (!cleanRequired) return false;

  const hasClean = paper.hasCleanVersion ?? paper.document?.hasCleanVersion;
  if (typeof hasClean === 'boolean') {
    return !hasClean;
  }

  return false;
}

export function PapersTable({ papers, isLoading, onAssign, onReassign, onStartSecondRound, onManageDocument }: PapersTableProps) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const toggleRow = (paperId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [paperId]: !prev[paperId],
    }));
  };

  if (isLoading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-gray-500">
        <div className="inline-flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          Cargando ponencias...
        </div>
      </div>
    );
  }

  if (papers.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white px-8 py-16 text-center shadow-sm">
        <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-slate-100">
          <FileSearch className="h-12 w-12 text-slate-400" />
        </div>
        <h3 className="mx-auto max-w-md text-4xl font-bold leading-tight text-[#1e3a5f]">
          No hay ponencias registradas para este evento.
        </h3>
        <p className="mx-auto mt-4 max-w-md text-lg text-gray-500">
          Las ponencias apareceran aqui cuando los autores completen su registro.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="w-full overflow-x-hidden">
        <Table className="w-full text-left whitespace-nowrap">
          <TableHeader className="bg-gray-50">
            <TableRow>
              <TableHead className="w-12 text-center" />
              <TableHead className="hidden min-w-[160px] md:table-cell">Folio</TableHead>
              <TableHead className="min-w-0">Titulo de ponencia</TableHead>
              <TableHead className="w-[11%]">Estado</TableHead>
              <TableHead className="hidden w-[11%] lg:table-cell">Documento</TableHead>
              <TableHead className="hidden min-w-[260px] lg:table-cell">Evaluadores asignados</TableHead>
              <TableHead className="w-[88px] text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {papers.map((paper) => {
              const max = paper.evaluatorsMax || 3;
              const percent = Math.min(100, Math.round((paper.evaluatorsCount / max) * 100));
              const isFinal = paper.status === 'APPROVED_TO_PAY' || paper.status === 'REJECTED';
              const isCorrectionRequested = paper.status === 'REVISION_REQUESTED';
              const docStatus = resolveDocumentStatus(paper);
              const needsDocumentalReview = docStatus === 'PENDING_CLEAN';
              const canStartSecondRound = paper.canStartSecondRound === true;
              const assignBlockedByClean = resolveAssignBlock(paper);
              const assignDisabled = assignBlockedByClean || isCorrectionRequested;
              const isExpanded = Boolean(expandedRows[paper.id]);

              return (
                <Fragment key={paper.id}>
                  <TableRow className="cursor-default border-b border-slate-200 transition-colors hover:bg-slate-50">
                    <TableCell className="text-center">
                      <button
                        type="button"
                        aria-expanded={isExpanded}
                        aria-label="Expandir fila para ver detalles"
                        className={`rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-[#0F2644] ${
                          isExpanded ? 'bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700' : ''
                        }`}
                        onClick={() => toggleRow(paper.id)}
                      >
                        {isExpanded ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                      </button>
                    </TableCell>
                    <TableCell
                      className="hidden max-w-[180px] truncate font-mono text-xs font-semibold md:table-cell"
                      title={paper.folio}
                    >
                      {paper.folio}
                    </TableCell>
                    <TableCell className="max-w-[300px] truncate font-medium" title={paper.title}>
                      {paper.title}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{statusBadge(paper.status)}</TableCell>
                    <TableCell className="hidden whitespace-nowrap lg:table-cell">{documentBadge(docStatus)}</TableCell>
                    <TableCell className="hidden whitespace-nowrap lg:table-cell">
                      <div className="flex min-w-[240px] items-center gap-3">
                        <div className="min-w-[110px]">
                          <p className="text-sm font-semibold text-gray-700">
                            {paper.evaluatorsCount}/{max}
                          </p>
                          <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200">
                            <div
                              className={`h-1.5 rounded-full transition-all ${progressColor(paper)}`}
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>

                        {!isFinal && paper.evaluatorsCount === 0 ? (
                          assignBlockedByClean ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="cursor-not-allowed whitespace-nowrap border-[#c9a227] text-[#1e3a5f] opacity-50"
                                    disabled
                                  >
                                    <UserPlus className="mr-1 h-4 w-4" />
                                    Asignar
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                sideOffset={8}
                                className="z-50 max-w-[240px] rounded-md bg-slate-800 px-2 py-1 text-center text-xs text-white shadow-lg"
                              >
                                Debe cargar una versión CLEAN antes de asignar evaluadores.
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="shrink-0 whitespace-nowrap border-[#c9a227] text-[#1e3a5f]"
                              onClick={() => onAssign(paper)}
                              disabled={assignDisabled}
                            >
                              <UserPlus className="mr-1 h-4 w-4" />
                              Asignar
                            </Button>
                          )
                        ) : null}

                        {!isFinal && needsDocumentalReview && paper.evaluatorsCount > 0 ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="cursor-not-allowed whitespace-nowrap border-amber-300 text-amber-700 opacity-70"
                                  disabled
                                >
                                  CLEAN v2 pendiente
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              Primero revise la versiÃ³n REVISION y cargue/genere CLEAN v2 desde Gestionar versiones.
                            </TooltipContent>
                          </Tooltip>
                        ) : null}


                        {!isFinal && canStartSecondRound ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="shrink-0 whitespace-nowrap border-green-500 bg-green-50 text-green-700 hover:bg-green-100"
                            onClick={() => onStartSecondRound(paper)}
                          >
                            <PlayCircle className="mr-1 h-4 w-4" />
                            Iniciar Segunda Ronda
                          </Button>
                        ) : null}
                        {!isFinal && !isCorrectionRequested && !needsDocumentalReview && !canStartSecondRound && paper.evaluatorsCount > 0 ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="shrink-0 whitespace-nowrap border-[#1e3a5f] text-[#1e3a5f]"
                            onClick={() => onReassign(paper)}
                          >
                            <Users className="mr-1 h-4 w-4" />
                            Reasignar
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="outline"
                            className="shrink-0 transition-colors duration-200"
                            onClick={() => onManageDocument(paper)}
                            aria-label="Gestionar versiones del documento"
                          >
                            <FileStack className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          sideOffset={8}
                          className="z-50 max-w-[200px] rounded-md bg-slate-800 px-2 py-1 text-center text-xs text-white shadow-lg"
                        >
                          Gestionar versiones del documento
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {isExpanded ? (
                    <TableRow className="border-b border-slate-200 hover:bg-transparent">
                      <TableCell colSpan={7} className="bg-slate-50/50 p-4 shadow-inner">
                        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                          <div>
                            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                              Autor / Institucion
                            </p>
                            <p className="text-slate-800">{paper.author.name}</p>
                            <p className="text-slate-800">{paper.author.institution}</p>
                          </div>
                          <div>
                            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                              Unidad academica
                            </p>
                            <p className="text-slate-800">{paper.academicUnit}</p>
                          </div>
                          <div className="md:hidden">
                            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Folio</p>
                            <p className="text-slate-800">{paper.folio}</p>
                          </div>
                          <div className="lg:hidden">
                            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Documento</p>
                            <div className="pt-1">{documentBadge(docStatus)}</div>
                          </div>
                          <div className="lg:hidden">
                            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                              Evaluadores asignados
                            </p>
                            <div className="max-w-[240px]">
                              <p className="text-sm font-semibold text-slate-800">
                                {paper.evaluatorsCount}/{max}
                              </p>
                              <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200">
                                <div
                                  className={`h-1.5 rounded-full transition-all ${progressColor(paper)}`}
                                  style={{ width: `${percent}%` }}
                                />
                              </div>
                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                {!isFinal && paper.evaluatorsCount === 0 ? (
                                  assignBlockedByClean ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex">
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="cursor-not-allowed whitespace-nowrap border-[#c9a227] text-[#1e3a5f] opacity-50"
                                            disabled
                                          >
                                            <UserPlus className="mr-1 h-4 w-4" />
                                            Asignar
                                          </Button>
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent
                                        side="top"
                                        sideOffset={8}
                                        className="z-50 max-w-[240px] rounded-md bg-slate-800 px-2 py-1 text-center text-xs text-white shadow-lg"
                                      >
                                        Debe cargar una versión CLEAN antes de asignar evaluadores.
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="shrink-0 whitespace-nowrap border-[#c9a227] text-[#1e3a5f]"
                                      onClick={() => onAssign(paper)}
                                      disabled={assignDisabled}
                                    >
                                      <UserPlus className="mr-1 h-4 w-4" />
                                      Asignar
                                    </Button>
                                  )
                                ) : null}
                                {!isFinal && needsDocumentalReview && paper.evaluatorsCount > 0 ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="cursor-not-allowed whitespace-nowrap border-amber-300 text-amber-700 opacity-70"
                                          disabled
                                        >
                                          CLEAN v2 pendiente
                                        </Button>
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                      Primero revise la versiÃ³n REVISION y cargue/genere CLEAN v2 desde Gestionar versiones.
                                    </TooltipContent>
                                  </Tooltip>
                                ) : null}

                        {!isFinal && canStartSecondRound ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="shrink-0 whitespace-nowrap border-green-500 bg-green-50 text-green-700 hover:bg-green-100"
                            onClick={() => onStartSecondRound(paper)}
                          >
                            <PlayCircle className="mr-1 h-4 w-4" />
                            Iniciar Segunda Ronda
                          </Button>
                        ) : null}
                        {!isFinal && !isCorrectionRequested && !needsDocumentalReview && !canStartSecondRound && paper.evaluatorsCount > 0 ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="shrink-0 whitespace-nowrap border-[#1e3a5f] text-[#1e3a5f]"
                                    onClick={() => onReassign(paper)}
                                  >
                                    <Users className="mr-1 h-4 w-4" />
                                    Reasignar
                                  </Button>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
